import { faArrowRight } from "@imaginelearning/mps-icon-library";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { IAlp } from "@imaginelearning/mps-api-service";
import {
  UxAvatar,
  UxAvatarEnum,
  UxAvatarPriorityEnum,
  UxAvatarStyleEnum,
  UxIcon,
  UxProgress,
  UxPriorityEnum
} from "@imaginelearning/mps-ux-component-library";
import React, { useContext } from "react";
import { Button, Card, Col, Row } from "react-bootstrap";
import { useNavigate } from "react-router-dom";
import getCurrentLessonIcon, { getk5LessonIcon } from "../common/icons";
import { AlpContext } from "../my-assignments-container";
import "./my-assignment-tile.scss";
import utcDateHandler from "../common/date-convertor";
import { assignmentProgess } from "../course-map-components/utils";
import TypeBadge from "./typebadge";
import useTheme from "../../../hooks/usetheme";
import useGradeRange from "../../../hooks/usegraderange";
import useBreakpoint from "../../liftoff/hooks/useBreakpoint";

interface MyAssignmentsTileProps {
  alp: IAlp;
  subjectName?: string;
}

function MyAssignmentsTile(props: MyAssignmentsTileProps) {
  const { alp, subjectName: propSubjectName } = props;
  const { assignmentInfo, subject } = alp;
  const { title, dueDate, lessons, ilpID, contentType, assessments, assignmentID } = assignmentInfo;
  const subjectName = propSubjectName ?? subject;

  const navigate = useNavigate();
  const { theme } = useTheme();
  const { isKto5 } = useGradeRange();
  const { setIlpId, setContentType } = useContext(AlpContext);
  const { isSmallScreen } = useBreakpoint();

  const formattedDueDate = React.useMemo(() => {
    const utcDate = utcDateHandler(dueDate);
    if (!utcDate || utcDate.split("T")[0] === "9999-12-31") {
      return null;
    }
    const date = new Date(utcDate);
    const options: Intl.DateTimeFormatOptions = { month: "long", day: "numeric", year: "numeric" };
    return date.toLocaleDateString("en-us", options);
  }, [dueDate]);

  const avatarIcon = React.useMemo(() => {
    if (!assignmentInfo.icon) return null;
    return (UxAvatarEnum as Record<string, UxAvatarEnum>)[assignmentInfo.icon.toUpperCase()];
  }, [assignmentInfo.icon]);

  const handleNavigation = React.useCallback(() => {
    setIlpId(ilpID);
    setContentType(contentType);
    navigate(`/assignments/${ilpID}`);
  }, [setIlpId, ilpID, setContentType, contentType, navigate]);

  const getSubjectIcon = () => {
    const iconFunction = isKto5 ? getk5LessonIcon : getCurrentLessonIcon;
    return <UxIcon icon={iconFunction(subjectName)} className="myassignment_tile_subject_icon" theme={theme} />;
  };

  const getSubjectLabel = () => (subjectName === "SEL" ? "PURPOSE" : subjectName.toUpperCase());

  const getProgressValue = () => assignmentProgess(contentType, alp.lessonAttempts, lessons, assessments);

  const getButtonText = () => (isKto5 ? "GO" : "Start");

  const subjectRowClass = `text-center myassignment_tile_subject_row myassignment_tile_${subjectName}`;
  const newAssignmentClass = `myassignment_tile_new_assignment${formattedDueDate ? "_padded" : ""}`;

  if (isSmallScreen) {
    return (
      <div className={`tiles ${isKto5 ? "k-5" : "six-12"}`} data-testid="myassignment-tile-unified-container">
        <Card body={false} className="myassignment_tile" key={assignmentID} onClick={handleNavigation}>
          {/* Row 1: Subject icon + name (left) + Type Badge (right) */}
          <Row className="myassignment_tile_row">
            <Col xs={8} className={subjectRowClass}>
              <div className="d-flex align-items-center">
                <span className="header-icon me-2">{getSubjectIcon()}</span>
                <div className="myassignment_tile_subject_row_sub">{getSubjectLabel()}</div>
              </div>
            </Col>
            <Col xs={4} className="text-end" data-testid="myassignment-tile-assignment-type-badge">
              <h3 className="heading-style-h5">
                <TypeBadge contentType={contentType} />
              </h3>
            </Col>
          </Row>

          {/* Row 2: Title only (max 2 lines with ellipsis) */}
          <Row className="myassignment_tile_row">
            <Col xs={12} className="myassignment_tile_title_row">
              <h4 className="myassignment_tile_ellipsis">{title}</h4>
            </Col>
          </Row>

          {/* Row 3: Column 1 - New! + Due Date + Progress, Column 2 - Avatar + Button */}
          <Row className="myassignment_tile_row">
            <Col xs={8} className="myassignment_tile_title_row">
              <div className="d-flex flex-column myassignment_tile_bottom_content">
                {alp.isNew && <div className={newAssignmentClass}>New!</div>}
                {formattedDueDate && (
                  <div className="myassignment_tile_due_date myassignment_tile_ellipsis">
                    <span className="due-date-label">DUE DATE:</span>
                    <br />
                    <span className="due-date-value">{formattedDueDate}</span>
                  </div>
                )}
                <div className="mt-2">
                  <UxProgress
                    variant={UxPriorityEnum.SUCCESS}
                    value={getProgressValue()}
                    className="myassignment_tile_progress_bar"
                  />
                </div>
              </div>
            </Col>
            <Col xs={4} className="myassignment_tile_third_col">
              {avatarIcon && (
                <div className="mb-2">
                  <UxAvatar
                    avatar={avatarIcon as UxAvatarEnum}
                    avatarType={UxAvatarPriorityEnum.DEFAULT}
                    avatarStyle={UxAvatarStyleEnum.BODY}
                    className="d-flex myassignment_tile_avatar"
                    theme={theme}
                  />
                </div>
              )}
              <Button
                onClick={handleNavigation}
                className="myassignment_tile_go_button"
                data-testid="myassignment-tile-go-button"
              >
                {getButtonText()}
                {isKto5 && (
                  <span className="myassignment_tile_go_arrow">
                    <FontAwesomeIcon icon={faArrowRight} />
                  </span>
                )}
              </Button>
            </Col>
          </Row>
        </Card>
      </div>
    );
  }

  // Regular (desktop) layout
  return (
    <div className={`tiles ${isKto5 ? "k-5" : "six-12"}`} data-testid="myassignment-tile-unified-container">
      <Card body={false} className="myassignment_tile" key={assignmentID} onClick={handleNavigation}>
        <Row className="myassignment_tile_row">
          <Col xs={2} className={subjectRowClass}>
            <span className="header-icon">{getSubjectIcon()}</span>
            <div className="myassignment_tile_subject_row_sub">{getSubjectLabel()}</div>
          </Col>
          <Col xs={7} className="myassignment_tile_title_row">
            <Row className="myassignment_tile_heading1">
              <Col xs={10} data-testid="myassignment-tile-assignment-type-badge">
                <h3 className="heading-style-h5">
                  <TypeBadge contentType={contentType} />
                </h3>
              </Col>
            </Row>
            <Row className="myassignment_tile_heading1">
              <Col xs={12}>
                <h4 className="myassignment_tile_ellipsis">{title}</h4>
                <div className="myassignment_tile_due_date myassignment_tile_ellipsis">
                  {formattedDueDate && `DUE DATE: ${formattedDueDate}`}
                  {alp.isNew && <span className={newAssignmentClass}>New!</span>}
                </div>
              </Col>
            </Row>
            <Row className="align-items-center">
              <Col xs={12}>
                <UxProgress
                  variant={UxPriorityEnum.SUCCESS}
                  value={getProgressValue()}
                  className="myassignment_tile_progress_bar"
                />
              </Col>
            </Row>
          </Col>
          <Col xs={3} className="text-center p-0">
            <div className="myassignment_tile_icon_block">
              <div className="myassignment_tile_avatars margin_b">
                {avatarIcon && (
                  <UxAvatar
                    avatar={avatarIcon as UxAvatarEnum}
                    avatarType={UxAvatarPriorityEnum.DEFAULT}
                    avatarStyle={UxAvatarStyleEnum.BODY}
                    className="d-flex myassignment_tile_avatar"
                    theme={theme}
                  />
                )}
              </div>
              <div>
                <Button
                  onClick={handleNavigation}
                  className="myassignment_tile_go_button"
                  data-testid="myassignment-tile-go-button"
                >
                  {getButtonText()}
                  {isKto5 && (
                    <span className="myassignment_tile_go_arrow">
                      <FontAwesomeIcon icon={faArrowRight} />
                    </span>
                  )}
                </Button>
              </div>
            </div>
          </Col>
        </Row>
      </Card>
    </div>
  );
}

export default MyAssignmentsTile;
